package com.zi.support.web

import com.zi.support.shared.InsightPeriod

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.MouseEvent

/**
 * The Insights views (Calls, Tickets, Issues): a figures strip, then SVG charts (line, bars, doughnut) and ranked
 * tables in a reporting style (`.ins-*` in styles.css). Every mark carries `data-tip`; [bind] shows it in one floating
 * tooltip while hovered. Numbers in [data-count] tick up, lines draw in and bars rise when [animate] is set.
 */
internal object Infographic {
    /** One figure in the strip; [delta] is optional context ("▲ 12% vs last week"), coloured by [trend] up/down; [spark] is one value per day. */
    data class Figure(val label: String, val value: String, val delta: String = "", val trend: String = "", val count: Int? = null, val spark: List<Int> = emptyList())
    /** A line or bar series. */
    data class Series(val name: String, val color: String, val values: List<Int>)
    /** A slice of a doughnut. */
    data class Part(val label: String, val value: Int, val color: String)

    val PALETTE = listOf("#2563eb", "#0d9488", "#d97706", "#7c3aed", "#db2777", "#64748b")

    fun section(title: String, subtitle: String, body: String, animate: Boolean) =
        "<section class=\"ins${if (animate) " ins-animate" else ""}\" aria-label=\"${escape(title)}\"><header class=\"ins-head\"><h3>${escape(title)}</h3><p>$subtitle</p></header><div class=\"ins-grid\">$body</div></section>"

    fun figures(items: List<Figure>) = "<div class=\"ins-card ins-figures\" style=\"--n:${items.size}\">" + items.joinToString("") { f ->
        val value = if (f.count != null) "<span data-count=\"${f.count}\">${escape(f.value)}</span>" else escape(f.value)
        "<div class=\"ins-figure\"><span class=\"ins-label\">${escape(f.label)}</span><strong>$value</strong>" +
            (if (f.delta.isBlank()) "" else "<span class=\"ins-delta ${f.trend}\">${escape(f.delta)}</span>") + spark(f.spark) + "</div>"
    } + "</div>"

    /** A small trend line under a figure; nothing for fewer than two days. */
    private fun spark(values: List<Int>): String {
        if (values.size < 2) return ""
        val max = values.maxOrNull()!!.coerceAtLeast(1)
        val points = values.mapIndexed { i, v -> "${i * 100.0 / values.lastIndex},${27 - 25.0 * v / max}" }.joinToString(" ")
        return "<svg class=\"ins-spark\" viewBox=\"0 0 100 28\" preserveAspectRatio=\"none\" aria-hidden=\"true\"><polygon points=\"0,28 $points 100,28\"/><polyline points=\"$points\"/></svg>"
    }

    private fun card(title: String, subtitle: String, span: String, body: String, legend: List<Pair<String, String>> = emptyList()) =
        "<div class=\"ins-card $span\"><div class=\"ins-card-head\"><div><h4>${escape(title)}</h4>${if (subtitle.isBlank()) "" else "<p>${escape(subtitle)}</p>"}</div>" +
            (if (legend.isEmpty()) "" else "<div class=\"ins-legend\">" + legend.joinToString("") { (name, color) -> "<span><i style=\"background:$color\"></i>${escape(name)}</span>" } + "</div>") +
            "</div>$body</div>"

    /** Axis top: four even, round steps (0, 5, 10, 15, 20 rather than 0, 12, 25, 37, 50). */
    private fun niceMax(max: Int): Int {
        val step = listOf(1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50, 60, 80, 100, 120, 150, 200, 250, 300, 400, 500, 1000, 2000, 5000).firstOrNull { it * 4 >= max } ?: ((max + 3) / 4)
        return step * 4
    }

    /** A smooth curve through the points (Catmull-Rom as cubic Béziers), never overshooting below the baseline. */
    private fun smooth(points: List<Pair<Double, Double>>, floor: Double): String {
        if (points.size < 3) return "M" + points.joinToString(" L") { "${it.first},${it.second}" }
        val d = StringBuilder("M${points[0].first},${points[0].second}")
        for (i in 0 until points.lastIndex) {
            val p0 = points[maxOf(i - 1, 0)]; val p1 = points[i]; val p2 = points[i + 1]; val p3 = points[minOf(i + 2, points.lastIndex)]
            val c1y = (p1.second + (p2.second - p0.second) / 6).coerceAtMost(floor); val c2y = (p2.second - (p3.second - p1.second) / 6).coerceAtMost(floor)
            d.append(" C${p1.first + (p2.first - p0.first) / 6},$c1y ${p2.first - (p3.first - p1.first) / 6},$c2y ${p2.first},${p2.second}")
        }
        return d.toString()
    }

    private fun gridAndAxis(w: Double, h: Double, left: Double, top: Double, max: Int) = (0..4).joinToString("") { n ->
        val y = top + h - h * n / 4
        "<line class=\"ins-grid-line\" x1=\"$left\" x2=\"${left + w}\" y1=\"$y\" y2=\"$y\"/><text class=\"ins-axis\" x=\"${left - 8}\" y=\"${y + 4}\" text-anchor=\"end\">${max * n / 4}</text>"
    }

    /** Line chart over [labels] (e.g. days) for each series; hovering a day shows every series' value. */
    fun line(title: String, subtitle: String, labels: List<String>, series: List<Series>, span: String = "ins-span-2", labelEvery: Int = maxOf(1, labels.size / 7)): String {
        val left = 40.0; val top = 12.0; val w = 640.0; val h = 190.0
        val max = niceMax(series.flatMap { it.values }.maxOrNull() ?: 0)
        val step = if (labels.size > 1) w / (labels.size - 1) else w
        fun x(i: Int) = left + i * step
        fun y(v: Int) = top + h - h * v / max
        val paths = series.mapIndexed { si, s ->
            val curve = smooth(s.values.mapIndexed { i, v -> x(i) to y(v) }, top + h)
            val area = if (si == 0) "<path class=\"ins-area\" d=\"$curve L${x(s.values.lastIndex)},${top + h} L${x(0)},${top + h} Z\" style=\"fill:${s.color}\"/>" else ""
            area + "<path class=\"ins-line\" pathLength=\"1\" d=\"$curve\" style=\"stroke:${s.color}\"/>" +
                // A year of daily dots is just noise; the hover still reads each day.
                if (labels.size > 62) "" else s.values.mapIndexed { i, v -> "<circle class=\"ins-dot\" data-col=\"$i\" cx=\"${x(i)}\" cy=\"${y(v)}\" r=\"3.5\" style=\"stroke:${s.color}\"/>" }.joinToString("")
        }.joinToString("")
        // Skip a regular label that would sit on top of the last one.
        val xLabels = labels.mapIndexed { i, l -> if (i % labelEvery == 0 && labels.lastIndex - i >= (labelEvery + 1) / 2 || i == labels.lastIndex) "<text class=\"ins-axis\" x=\"${x(i)}\" y=\"${top + h + 20}\" text-anchor=\"middle\">${escape(l)}</text>" else "" }.joinToString("")
        val hits = labels.mapIndexed { i, l ->
            val tip = escape(l) + series.joinToString("") { s -> "\n${s.name}: ${s.values.getOrElse(i) { 0 }}" }
            "<rect class=\"ins-hit\" data-col=\"$i\" data-tip=\"$tip\" x=\"${x(i) - step / 2}\" y=\"$top\" width=\"$step\" height=\"$h\"/>"
        }.joinToString("")
        val svg = "<svg class=\"ins-chart\" viewBox=\"0 0 ${left + w + 16} ${top + h + 30}\" role=\"img\" aria-label=\"${escape(title)}\">${gridAndAxis(w, h, left, top, max)}$paths$xLabels$hits</svg>"
        return card(title, subtitle, span, svg, series.map { it.name to it.color })
    }

    /** Vertical bars, one per label; hover shows the label and value. Stacked when there are several series. */
    fun bars(title: String, subtitle: String, labels: List<String>, series: List<Series>, span: String = "", labelEvery: Int = 1, empty: String = ""): String {
        // A one-column card is half as wide, so draw it narrower to keep the text the same size.
        val left = 40.0; val top = 12.0; val w = if (span.isBlank()) 300.0 else 640.0; val h = 190.0
        val totals = labels.indices.map { i -> series.sumOf { it.values.getOrElse(i) { 0 } } }
        if (empty.isNotBlank() && totals.all { it == 0 }) return card(title, subtitle, span, "<p class=\"ins-empty\">${escape(empty)}</p>")
        val max = niceMax(totals.maxOrNull() ?: 0)
        val slot = w / labels.size; val bw = (slot * 0.62).coerceAtMost(28.0)
        val bars = labels.indices.joinToString("") { i ->
            var base = top + h
            val stack = series.joinToString("") { s ->
                val v = s.values.getOrElse(i) { 0 }; val bh = h * v / max; base -= bh
                if (v == 0) "" else "<rect class=\"ins-bar\" x=\"${left + i * slot + (slot - bw) / 2}\" y=\"$base\" width=\"$bw\" height=\"$bh\" rx=\"2\" style=\"fill:${s.color};--d:${i * 25}ms\"/>"
            }
            val tip = escape(labels[i]) + if (series.size == 1) ": ${totals[i]}" else series.joinToString("") { s -> "\n${s.name}: ${s.values.getOrElse(i) { 0 }}" }
            stack + "<rect class=\"ins-hit\" data-tip=\"$tip\" x=\"${left + i * slot}\" y=\"$top\" width=\"$slot\" height=\"$h\"/>" +
                (if (i % labelEvery == 0) "<text class=\"ins-axis\" x=\"${left + i * slot + slot / 2}\" y=\"${top + h + 20}\" text-anchor=\"middle\">${escape(labels[i])}</text>" else "")
        }
        val svg = "<svg class=\"ins-chart\" viewBox=\"0 0 ${left + w + 16} ${top + h + 30}\" role=\"img\" aria-label=\"${escape(title)}\">${gridAndAxis(w, h, left, top, max)}$bars</svg>"
        return card(title, subtitle, span, svg, if (series.size > 1) series.map { it.name to it.color } else emptyList())
    }

    /** Doughnut with the total in the middle and a legend of value + share; hover a slice for its figures. */
    fun donut(title: String, subtitle: String, parts: List<Part>, centerLabel: String, span: String = ""): String {
        val total = parts.sumOf { it.value }
        val r = 54.0; val c = 2 * kotlin.math.PI * r
        var offset = 0.0
        val slices = parts.filter { it.value > 0 }.joinToString("") { p ->
            val len = c * p.value / total.coerceAtLeast(1)
            val tip = "${escape(p.label)}: ${p.value} (${p.value * 100 / total.coerceAtLeast(1)}%)"
            val slice = "<circle class=\"ins-slice\" data-tip=\"$tip\" cx=\"70\" cy=\"70\" r=\"$r\" style=\"stroke:${p.color};stroke-dasharray:${len - 1.5} ${c - len + 1.5};stroke-dashoffset:${-offset}\"/>"
            offset += len; slice
        }
        val svg = "<div class=\"ins-donut\"><svg viewBox=\"0 0 140 140\" role=\"img\" aria-label=\"${escape(title)}\"><circle class=\"ins-track\" cx=\"70\" cy=\"70\" r=\"$r\"/>$slices" +
            "<text class=\"ins-donut-total\" x=\"70\" y=\"68\" text-anchor=\"middle\">$total</text><text class=\"ins-axis\" x=\"70\" y=\"86\" text-anchor=\"middle\">${escape(centerLabel)}</text></svg>" +
            "<ul>" + parts.joinToString("") { p -> "<li data-tip=\"${escape(p.label)}: ${p.value}\"><i style=\"background:${p.color}\"></i><span>${escape(p.label)}</span><strong>${p.value}</strong><small>${if (total == 0) 0 else p.value * 100 / total}%</small></li>" } + "</ul></div>"
        return card(title, subtitle, span, svg)
    }

    /** "09:00". */
    fun hour(h: Int) = "${h.toString().padStart(2, '0')}:00"
    private val WEEKDAYS = listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun")

    /** Seven rows of 24 hourly counts, Monday first, from epoch millis (0 or less is skipped). */
    fun weekHours(times: List<Double>): List<List<Int>> {
        val grid = List(7) { IntArray(24) }
        times.filter { it > 0 }.forEach { m -> kotlin.js.Date(m).let { d -> grid[(d.getDay() + 6) % 7][d.getHours()]++ } }
        return grid.map { it.toList() }
    }

    /** Weekday × hour grid from [weekHours]; darker is busier, hover a cell for its count of [noun]. */
    fun heatmap(title: String, grid: List<List<Int>>, noun: String, color: String, span: String = "ins-span-2"): String {
        val left = 40.0; val top = 4.0; val cw = 26.0; val ch = 24.0
        val max = grid.flatten().maxOrNull()?.coerceAtLeast(1) ?: 1
        val busiest = grid.indices.flatMap { d -> grid[d].indices.map { h -> Triple(d, h, grid[d][h]) } }.maxByOrNull { it.third }?.takeIf { it.third > 0 }
        val cells = grid.mapIndexed { d, row ->
            "<text class=\"ins-axis\" x=\"${left - 8}\" y=\"${top + d * ch + ch / 2 + 2}\" text-anchor=\"end\">${WEEKDAYS[d]}</text>" + row.mapIndexed { h, v ->
                val fill = if (v == 0) "fill:var(--surface-subtle)" else "fill:$color;fill-opacity:${0.18 + 0.82 * v / max}"
                "<rect class=\"ins-cell\" data-tip=\"${WEEKDAYS[d]} ${hour(h)}–${hour(h + 1)}\n$v $noun\" x=\"${left + h * cw}\" y=\"${top + d * ch}\" width=\"${cw - 3}\" height=\"${ch - 3}\" rx=\"3\" style=\"$fill\"/>"
            }.joinToString("")
        }.joinToString("")
        val xLabels = (0 until 24 step 3).joinToString("") { h -> "<text class=\"ins-axis\" x=\"${left + h * cw + (cw - 3) / 2}\" y=\"${top + 7 * ch + 14}\" text-anchor=\"middle\">${hour(h)}</text>" }
        val scale = "<div class=\"ins-scale\" aria-hidden=\"true\"><span>Less</span>" + listOf(0.18, 0.45, 0.72, 1.0).joinToString("") { "<i style=\"background:$color;opacity:$it\"></i>" } + "<span>More</span></div>"
        val svg = "<svg class=\"ins-chart\" viewBox=\"0 0 ${left + 24 * cw + 8} ${top + 7 * ch + 22}\" role=\"img\" aria-label=\"${escape(title)}\">$cells$xLabels</svg>$scale"
        return card(title, busiest?.let { (d, h, v) -> "Busiest ${WEEKDAYS[d]} ${hour(h)} · $v $noun" } ?: "No $noun yet", span, svg)
    }

    /** Duration buckets for the "how long" charts. */
    val DURATIONS = listOf("< 1h" to 1.0, "1–4h" to 4.0, "4–24h" to 24.0, "1–3d" to 72.0, "3–7d" to 168.0, "7d+" to Double.MAX_VALUE)
    /** How many of [millis] fall in each of [DURATIONS]. */
    fun durationCounts(millis: List<Double>): List<Int> = DURATIONS.map { (_, hours) -> hours }.let { tops ->
        List(tops.size) { b -> millis.count { m -> tops.indexOfFirst { m / 3_600_000 < it } == b } }
    }

    /** Ranked table: position, name, count, share of [total] with a thin bar. */
    fun table(title: String, subtitle: String, rows: List<Pair<String, Int>>, total: Int, empty: String, span: String = ""): String {
        val max = rows.maxOfOrNull { it.second }?.coerceAtLeast(1) ?: 1
        val body = if (rows.isEmpty()) "<p class=\"ins-empty\">${escape(empty)}</p>" else "<table class=\"ins-table\"><tbody>" + rows.mapIndexed { i, (label, count) ->
            val share = if (total == 0) 0 else count * 100 / total
            "<tr data-tip=\"${escape(label)}: $count ($share%)\"><td class=\"ins-pos\">${i + 1}</td><td class=\"ins-name\"><span>${escape(label)}</span><span class=\"ins-meter\"><i style=\"width:${count * 100 / max}%\"></i></span></td><td class=\"ins-num\">$count</td><td class=\"ins-share\">$share%</td></tr>"
        }.joinToString("") + "</tbody></table>"
        return card(title, subtitle, span, body)
    }

    /** The Day / Week / Month / Year / Custom period above each Insights view; [from] and [to] are the custom dates (yyyy-MM-dd). */
    class Range(var kind: String = "week", var from: String = "", var to: String = "") {
        /** Days back from today to the period's first and last day, both included. */
        val first get() = bounds().first
        val last get() = bounds().second
        val days get() = first - last + 1
        /** Local midnight that starts the period, and the one after its last day. */
        val start get() = midnight(first)
        val end get() = midnight(last - 1)
        // Week, Month and Year run from Monday, the 1st or 1 January up to today (shared InsightPeriod).
        private fun bounds(): Pair<Int, Int> = when (kind) {
            "day" -> 0 to 0
            "custom" -> listOf(ago(from) ?: 6, ago(to) ?: 0).sortedDescending().let { it[0] to it[1] }
            else -> kotlin.js.Date().let { t -> InsightPeriod.firstDaysBack(kind, t.getFullYear(), t.getMonth() + 1, t.getDate()) } to 0
        }
        fun label() = when (kind) {
            "custom" -> "${shortDate(first)} – ${shortDate(last)}"
            else -> InsightPeriod.label(kind)
        }
        /** For "vs previous …" in [trend]: the same number of days just before, so to-date periods say "period". */
        fun noun() = when (kind) { "day" -> "day"; else -> "period" }
        /** One label per day, oldest first, for the line charts. */
        fun dayLabels() = (first downTo last).map { if (it == 0) "Today" else shortDate(it) }
        /** yyyy-MM-dd of the first and last day, for generateReport's from/to. */
        fun fromIso() = iso(first)
        fun toIso() = iso(last)
    }

    private const val DAY = 86_400_000.0
    private fun dateAgo(daysAgo: Int): kotlin.js.Date { val t = kotlin.js.Date(); return kotlin.js.Date(t.getFullYear(), t.getMonth(), t.getDate() - daysAgo) }
    fun midnight(daysAgo: Int) = dateAgo(daysAgo).getTime()
    private fun shortDate(daysAgo: Int) = dateAgo(daysAgo).asDynamic().toLocaleDateString(undefined, js("({day:'numeric',month:'short'})")) as String
    /** yyyy-MM-dd for a date input. */
    private fun iso(daysAgo: Int) = dateAgo(daysAgo).let { d -> "${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}" }
    /** Days back from today for a date input's value (local date), never in the future; null when blank or malformed. */
    private fun ago(value: String): Int? {
        val p = value.split("-").mapNotNull { it.toIntOrNull() }.takeIf { it.size == 3 } ?: return null
        return kotlin.math.round((midnight(0) - kotlin.js.Date(p[0], p[1] - 1, p[2]).getTime()) / DAY).toInt().coerceAtLeast(0)
    }

    /** The period picker; [bindRange] wires it after the HTML is in the page. */
    fun rangeControls(r: Range): String {
        val buttons = listOf("day" to "Day", "week" to "Week", "month" to "Month", "year" to "Year", "custom" to "Custom").joinToString("") { (k, l) ->
            "<button type=\"button\" data-range=\"$k\" aria-pressed=\"${k == r.kind}\"${if (k == r.kind) " class=\"active\"" else ""}>$l</button>"
        }
        val dates = if (r.kind != "custom") "" else listOf("from" to "From", "to" to "To").joinToString("") { (k, l) ->
            "<label class=\"date-filter\">$l<input type=\"date\" class=\"input\" data-range-date=\"$k\" value=\"${if (k == "from") r.from else r.to}\" max=\"${iso(0)}\"></label>"
        } + "<button type=\"button\" class=\"btn\" data-range-clear>Clear</button>"
        return "<div class=\"segmented\" role=\"group\" aria-label=\"Period\">$buttons</div>$dates"
    }

    fun bindRange(root: org.w3c.dom.Element, r: Range, onChange: () -> Unit) {
        root.querySelectorAll("[data-range]").let { n -> for (i in 0 until n.length) (n.item(i) as HTMLElement).let { b ->
            b.addEventListener("click", {
                val kind = b.getAttribute("data-range")!!
                if (kind != r.kind) { r.kind = kind; if (kind == "custom" && r.from.isBlank()) { r.from = iso(6); r.to = iso(0) }; onChange() }
            })
        } }
        // Clear: drop the custom range and go back to the default (Week).
        root.querySelectorAll("[data-range-clear]").let { n -> for (i in 0 until n.length) (n.item(i) as HTMLElement).addEventListener("click", {
            r.kind = Range().kind; r.from = ""; r.to = ""; onChange()
        }) }
        root.querySelectorAll("[data-range-date]").let { n -> for (i in 0 until n.length) (n.item(i) as org.w3c.dom.HTMLInputElement).let { input ->
            input.addEventListener("change", {
                // A cleared date falls back to the default (a week ago / today) instead of keeping the old figures.
                if (input.value.isBlank() || ago(input.value) != null) { if (input.getAttribute("data-range-date") == "from") r.from = input.value else r.to = input.value; onChange() }
            })
        } }
    }

    /** Shown in place of the charts while a period's records load, so old figures never pass for new ones. */
    fun loading(r: Range, controls: String) = "<div class=\"ins-controls\">$controls${rangeControls(r)}</div><div class=\"empty\"><span class=\"spinner-mini\"></span> Loading ${escape(r.label().lowercase())}…</div>"

    /** "Export PDF" next to the period picker; [bindExport] wires it. */
    const val EXPORT_BUTTON = "<button type=\"button\" class=\"btn ins-export\" data-ins-export><span class=\"material-symbols-rounded\" aria-hidden=\"true\">picture_as_pdf</span>Export PDF</button>"

    /** Builds [reportId] on the server (generateReport) for the period, plus [extra] params, and downloads the PDF. */
    fun bindExport(root: org.w3c.dom.Element, reportId: String, r: Range, extra: () -> Map<String, String> = { emptyMap() }) {
        val button = root.querySelector("[data-ins-export]") as? HTMLElement ?: return
        button.addEventListener("click", {
            button.setAttribute("disabled", ""); button.innerHTML = "<span class=\"spinner-mini\"></span> Building PDF…"
            val params = json("reportId" to reportId, "from" to r.fromIso(), "to" to r.toIso())
            extra().forEach { (k, v) -> params[k] = v }
            promise(FirebaseGateway.callable("generateReport", params), { result ->
                downloadPdf(result.data.pdfBase64 as String, result.data.fileName as String); toast("Report downloaded.")
                button.removeAttribute("disabled"); button.innerHTML = EXPORT_BUTTON.substringAfter(">").substringBeforeLast("<")
            }, { message -> toast(message, true); button.removeAttribute("disabled"); button.innerHTML = EXPORT_BUTTON.substringAfter(">").substringBeforeLast("<") })
        })
    }

    /** Saves a generateReport result (base64 PDF) as a download. */
    fun downloadPdf(base64: String, fileName: String) {
        if (MobileInsights.enabled) {
            MobileInsights.post(json("type" to "pdf", "base64" to base64, "fileName" to fileName))
            return
        }
        val toPdf = js("function (b64) { return new Blob([Uint8Array.from(atob(b64), function (c) { return c.charCodeAt(0) })], {type: 'application/pdf'}) }")
        val url = window.asDynamic().URL.createObjectURL(toPdf(base64)) as String
        val link = document.createElement("a").asDynamic()
        link.href = url; link.download = fileName; link.click()
        window.setTimeout({ window.asDynamic().URL.revokeObjectURL(url) }, 10000)
    }

    /** Shared figure helpers. */
    fun percent(part: Int, whole: Int) = if (whole == 0) 0 else (part * 100 + whole / 2) / whole
    fun trend(now: Int, before: Int, period: String): Pair<String, String> = when {
        before == 0 -> (if (now == 0) "" else "New this $period") to ""
        else -> { val c = (now - before) * 100 / before; "${if (c >= 0) "▲" else "▼"} ${kotlin.math.abs(c)}% vs previous $period" to (if (c >= 0) "up" else "down") }
    }

    /** "2d 4h", "3h 12m", "45m". */
    fun duration(millis: Double): String {
        val minutes = (millis / 60_000).toLong()
        return when {
            minutes >= 1440 -> "${minutes / 1440}d ${minutes % 1440 / 60}h"
            minutes >= 60 -> "${minutes / 60}h ${minutes % 60}m"
            else -> "${minutes}m"
        }
    }

    /** Hover tooltips for everything with data-tip inside [root], and the count-up when animating. */
    fun bind(root: HTMLElement) {
        val tip = (document.getElementById("ins-tip") as? HTMLElement) ?: (document.createElement("div") as HTMLElement).also {
            it.id = "ins-tip"; it.className = "ins-tip"; it.setAttribute("role", "tooltip"); document.body?.appendChild(it)
        }
        fun hide() { tip.classList.remove("on"); root.querySelectorAll(".ins-col-on").let { n -> for (i in 0 until n.length) (n.item(i) as HTMLElement).classList.remove("ins-col-on") } }
        val showTip: (org.w3c.dom.events.Event) -> Unit = show@{ e ->
            val ev = e as MouseEvent
            val target = (ev.target as? org.w3c.dom.Element)?.closest("[data-tip]") as? org.w3c.dom.Element
            if (target == null) { hide(); return@show }
            tip.innerHTML = target.getAttribute("data-tip").orEmpty().split("\n").mapIndexed { i, line -> if (i == 0) "<strong>${escape(line)}</strong>" else "<span>${escape(line)}</span>" }.joinToString("")
            tip.classList.add("on")
            val x = (ev.clientX + 14).coerceAtMost(window.innerWidth - tip.offsetWidth - 8).coerceAtLeast(8)
            tip.style.left = "${x}px"; tip.style.top = "${(ev.clientY - tip.offsetHeight - 12).coerceAtLeast(8)}px"
            // Line charts: highlight that day's points.
            target.getAttribute("data-col")?.let { col ->
                root.querySelectorAll(".ins-col-on").let { n -> for (i in 0 until n.length) (n.item(i) as HTMLElement).classList.remove("ins-col-on") }
                (target.parentNode as? org.w3c.dom.Element)?.querySelectorAll(".ins-dot[data-col=\"$col\"]")?.let { n -> for (i in 0 until n.length) (n.item(i) as org.w3c.dom.Element).classList.add("ins-col-on") }
            }
        }
        root.addEventListener("mousemove", showTip)
        // Touch devices have no hover: tapping the same chart marks exposes the same values.
        root.addEventListener("click", showTip)
        root.addEventListener("mouseleave", { hide() })
        if (root.classList.contains("ins-animate")) countUp(root)
    }

    /** Numbers in [data-count] tick up from zero over ~0.7s (skipped when the viewer prefers reduced motion). */
    private fun countUp(root: HTMLElement) {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
        val nodes = root.querySelectorAll("[data-count]")
        for (n in 0 until nodes.length) {
            val node = nodes.item(n) as HTMLElement
            val target = node.getAttribute("data-count")!!.toIntOrNull() ?: continue
            if (target <= 0) continue
            val start = window.performance.now()
            fun frame(now: Double) {
                val t = ((now - start) / 700.0).coerceIn(0.0, 1.0)
                node.textContent = kotlin.math.round(target * (1 - (1 - t) * (1 - t) * (1 - t))).toInt().toString()
                if (t < 1) window.requestAnimationFrame(::frame)
            }
            node.textContent = "0"
            window.requestAnimationFrame(::frame)
        }
    }
}
